// @flow

import React, {Component} from 'react';
import Sortable from 'react-sortablejs';
import classnames from 'classnames';

import {SORTABLE_DIRECTION, SORTABLE_FILTER, EVENT_TYPES, SORTABLE_CLASSNAME} from './constants';
import {checkIntersection} from './utils';
import './sortable.styl';

import type {SortableCommonProps} from './typedef';


const DIRECTION_CLASSES = {
    [SORTABLE_DIRECTION.HORIZONTAL]: 'sortable__list_horizontal',
    [SORTABLE_DIRECTION.VERTICAL]: 'sortable__list_vertical'
};

const highlightedClass = 'sortable__ghost_highlighted';
const OUTSIDE_SPACE = 120;

type Props = SortableCommonProps;

export class SortableList extends Component<Props> {
    cloneBefore = null;
    sortableInstance = null;
    containers: Array<HTMLElement> | null = null;
    dragElement: HTMLElement | null = null;
    lastIndex = -1;
    dragInside = true;

    static defaultProps = {
        isSingleItem: false,
        isEmpty: false,
        direction: SORTABLE_DIRECTION.HORIZONTAL,
        sortableTheme: '',
        sort: true,
        pull: true,
        put: null,
        removable: false,
        revertOriginalPosition: true,
        disabled: false,
        addDragToBody: false,
        onHighlight: () => undefined,
        fallbackTolerance: 1,
        delay: 20,
        multipleLists: false,
        mix: '',
        autoScrolling: false,
        defaultMovement: false
    };

    shouldComponentUpdate({activeListName, listName}: Props) {
        const elem = this.sortableInstance && this.sortableInstance.el;
        if (elem && activeListName !== this.props.activeListName) {
            const isActive = activeListName === listName;
            this.updateMovingList(isActive);
            return false;
        }
        return true;
    }

    componentWillUnmount() {
        /**
         * When `revertOnSpill` is equal `true` and component is destroying than throw an error.
         * TODO: Check this case again after updating SortableJS to the next version.
         */
        if (this.sortableInstance) {
            this.sortableInstance.option('revertOnSpill', false);
        }
    }

    updateMovingList(isActive: boolean) {
        const elem = this.sortableInstance && this.sortableInstance.el;
        if (elem) {
            if (isActive) {
                elem.classList.add('sortable__list_moving');
            } else {
                elem.classList.remove('sortable__list_moving');
            }
        }
    }

    onChange = (itemIds: Array<string>, sortable: ?Object, event: Object) => {
        const {type, from, to, item, newIndex, oldIndex} = event;
        const isUpdateType = type === EVENT_TYPES.UPDATE;

        if (this.dragInside || !isUpdateType) {
            this.props.onChange({
                itemIds,
                isSingleListSorted: from === to,
                movableItemId: item.dataset.id,
                newIndex,
                oldIndex,
                type: isUpdateType ? EVENT_TYPES.UPDATE : EVENT_TYPES.ADD
            });
        } else if (this.props.removable) {
            this.props.onChange({
                itemIds: itemIds.filter(id => id !== item.dataset.id),
                isSingleListSorted: from === to,
                movableItemId: item.dataset.id,
                newIndex,
                oldIndex,
                type: EVENT_TYPES.REMOVE
            });
        }
    };

    checkInSide(elem: ?Element, x: number, y: number) {
        if (elem) {
            const elemRect = elem.getBoundingClientRect();
            if (this.props.direction === SORTABLE_DIRECTION.HORIZONTAL) {
                return y >= elemRect.top - elemRect.height / 2
                    && y <= elemRect.top + elemRect.height + OUTSIDE_SPACE;
            } else {
                return x >= elemRect.left - elemRect.width / 2
                    && x <= elemRect.left + elemRect.width + OUTSIDE_SPACE;
            }
        }
        return true;
    }

    checkIntersection() {
        if (this.containers && this.dragElement) {
            const dragElementRect = this.dragElement.getBoundingClientRect();
            const {width, height} = dragElementRect;

            if (!width || !height) {
                return this.dragInside;
            }

            return (this.containers || []).some(el => checkIntersection(dragElementRect, el.getBoundingClientRect()));
        } else {
            return true;
        }
    }

    isDragInsideMultipleLists = (elem: HTMLElement): boolean => {
        // When the item is out of the viewport in Firefox, elem is Document, and it has no `closest` method
        return this.checkIntersection() && elem.closest && !!elem.closest(`.${SORTABLE_CLASSNAME}`);
    };

    getDragCoordinates(dragElement: HTMLElement) {
        const dragRect = dragElement.getBoundingClientRect();
        const clientX = dragRect.left + dragRect.width / 2;
        const clientY = dragRect.top + dragRect.height / 2;
        return {clientX, clientY, dragRect};
    }

    addMoveListener() {
        window.addEventListener('mousemove', this.onMouseMove);
    }

    removeMoveListener() {
        window.removeEventListener('mousemove', this.onMouseMove);
    }

    isDragInside = (event: SyntheticEvent<>) => {
        let dragInside = false;
        if (this.props.multipleLists && event) {
            dragInside = this.isDragInsideMultipleLists(((event.target: any): HTMLElement));
        } else {
            const element = this.dragElement;
            const sortableInstance = this.sortableInstance;
            if (element && sortableInstance) {
                const elem = sortableInstance?.el || null;
                const {clientX, clientY} = this.getDragCoordinates(element);
                dragInside = clientX || clientY ? this.checkInSide(elem, clientX, clientY) : this.dragInside;
            }
        }

        return dragInside;
    };

    onMouseMove = (event: SyntheticEvent<>) => {
        const {onMove} = this.props;
        const {sortableInstance} = this;
        const element = this.dragElement;
        if (element && sortableInstance) {
            const dragInside = this.isDragInside(event);
            const elem = sortableInstance.el || null;
            const {clientX, clientY, dragRect} = this.getDragCoordinates(element);

            if (!dragInside && (clientX || clientY) && elem) {
                    const dragged = elem.querySelector(`.${sortableInstance.option('ghostClass')}`);
                    if (dragged && dragged.classList.contains(highlightedClass)) {
                        dragged.classList.remove(highlightedClass);
                        const newIndex = sortableInstance.constructor.utils.index(dragged, '.sortable__item');
                        this.props.onHighlight(false, newIndex);
                    }
                }

            if (dragInside !== this.dragInside) {
                this.dragInside = dragInside;

                if (this.props.removable) {
                    if (!this.dragInside) {
                        element.classList.add('sortable__remove');
                    } else {
                        element.classList.remove('sortable__remove');
                    }
                }
            }

            if (onMove) {
                onMove(dragRect);
            }
        }
    };

    onScroll = (offsetX: number, offsetY: number) => {
        const {onScroll} = this.props.scroll || {};
        if (onScroll) {
            onScroll(this.props.direction === SORTABLE_DIRECTION.HORIZONTAL ? offsetX : offsetY);
        }
    };

    render() {
        const {
            listName,
            sort,
            pull,
            put,
            disabled,
            fallbackClass,
            containerClass,
            onHighlight,
            updateActiveList,
            mix,
            fallbackTolerance,
            delay,
            addDragToBody,
            revertOriginalPosition,
            autoScrolling,
            onMove,
            handle
        } = this.props;

        const classes = classnames(
            'sortable sortable__list',
            DIRECTION_CLASSES[this.props.direction],
            this.props.sortableTheme,
            {
                sortable__list_empty: this.props.isEmpty,
                sortable__list_disabled: disabled,
                [mix]: mix
            }
        );

        return (
            <Sortable
                ref={c => this.sortableInstance = c && c.sortable}
                options={{
                    group: {
                        name: listName,
                        pull,
                        put
                    },
                    sort,
                    disabled,
                    draggable: '.sortable__item',
                    ghostClass: 'sortable__ghost',
                    chosenClass: 'sortable__chosen',
                    dragClass: 'sortable__drag',
                    fallbackClass: 'sortable__fallback',
                    forceFallback: true,
                    fallbackTolerance,
                    filter: '.' + SORTABLE_FILTER,
                    preventOnFilter: false,
                    fallbackOnBody: addDragToBody,
                    scroll: autoScrolling,
                    scrollFn: this.onScroll,
                    handle,
                    delay,
                    revertOnSpill: revertOriginalPosition,
                    removeCloneOnHide: false,
                    onStart: event => {
                        const parent = event.item.parentNode;
                        if (parent === event.clone.parentNode) {
                            parent.insertBefore(event.item, event.clone);
                        }

                        if (document.documentElement) {
                            document.documentElement.classList.add('on-drag');
                        }
                        const {from, oldIndex} = event;
                        this.lastIndex = oldIndex;
                        if (this.sortableInstance) {
                            const {getScroll} = this.props.scroll || {};
                            if (getScroll) {
                                this.sortableInstance.option('scroll', getScroll());
                            }
                            this.dragElement = document
                                .querySelector(`.${this.sortableInstance.option('fallbackClass')}`);
                        }

                        if (this.dragElement && fallbackClass) {
                            this.dragElement.classList.add(fallbackClass);
                        }
                        if (this.cloneBefore) {
                            this.cloneBefore.classList.add('sortable__clone_before');
                        }
                        if (this.props.isSingleItem) {
                            from.classList.add('sortable__list_single');
                        }
                        if (containerClass) {
                            this.containers = Array.from(document.querySelectorAll(`.${containerClass}`));
                        }
                        if (updateActiveList) {
                            updateActiveList(listName);
                        } else {
                            this.updateMovingList(true);
                        }

                        this.addMoveListener();

                        if (this.props.onStartDrag) {
                            this.props.onStartDrag(event);
                        }
                    },
                    onEnd: event => {
                        if (document.documentElement) {
                            document.documentElement.classList.remove('on-drag');
                        }
                        if (this.dragElement && fallbackClass) {
                            this.dragElement.classList.remove(fallbackClass);
                        }
                        this.dragElement = null;
                        this.containers = null;
                        this.removeMoveListener();
                        const {item, from, oldIndex, newIndex} = event;
                        from.classList.remove('sortable__list_single');
                        this.lastIndex = -1;
                        onHighlight(false, newIndex);
                        item.classList.remove(highlightedClass);
                        if (this.cloneBefore) {
                            this.cloneBefore.classList.remove('sortable__clone_before');
                        }
                        if (updateActiveList) {
                            updateActiveList('');
                        } else {
                            this.updateMovingList(false);
                        }
                        if (oldIndex === newIndex && !this.dragInside && this.sortableInstance) {
                            this.onChange(
                                this.sortableInstance.toArray(),
                                this.sortableInstance,
                                {...event, type: EVENT_TYPES.UPDATE}
                            );
                        }

                        if (this.props.onEndDrag) {
                            this.props.onEndDrag(event);
                        }
                    },
                    onMove: (event, originalEvent) => {
                        const {dragged, relatedRect, related, from, to} = event;
                        const {rootEl, clientX, clientY} = originalEvent;
                        this.dragInside = this.isDragInside(event);

                        const isHighlighted = dragged.classList.contains(highlightedClass);

                        const isHorizontal = rootEl.classList.contains('sortable__list_horizontal');
                        const isVerticalInsertAfter = clientY > relatedRect.top + relatedRect.height / 2 ? 1 : -1;
                        const isHorizontalInsertAfter = clientX > relatedRect.left + relatedRect.width / 2 ? 1 : -1;

                        const movementResult = isHorizontal ? isHorizontalInsertAfter : isVerticalInsertAfter;

                        const shouldShowBar = !related.classList.contains('sortable__clone') ||
                            related.classList.contains('sortable__clone_before') && movementResult === 1
                            && (this.dragInside || from !== to);

                        if (this.sortableInstance) {
                            const newIndex = this.sortableInstance.constructor.utils.index(dragged, '.sortable__item');
                            if (isHighlighted !== shouldShowBar || this.lastIndex !== newIndex) {
                                onHighlight(shouldShowBar, newIndex);
                            }
                            this.lastIndex = newIndex;
                        }

                        if (shouldShowBar) {
                            dragged.classList.add(highlightedClass);
                        } else {
                            dragged.classList.remove(highlightedClass);
                        }

                        if (onMove && this.dragElement) {
                            const {dragRect} = this.getDragCoordinates(this.dragElement);
                            onMove(dragRect, event);
                        }

                        if (this.props.defaultMovement) {
                            return null;
                        }

                        // 1 - insert after target; -1 - insert before target; false - cancel movement
                        return movementResult;
                    },
                    onClone: ({clone}) => {
                        clone.classList.add('sortable__clone');
                        this.cloneBefore = clone.previousSibling;
                    }
                }}
                className={classes}
                onChange={this.onChange}>
                {this.props.children}
            </Sortable>
        );
    }
}

            // @pb/generate-flow-typed Webpack Loader:
            // Duplicates all internal dependencies imports
            // to be sure all deps are parsed by this loader
            // cause Babel loader removes `import type ...`
            // and e.g. 'typedef.js' files are not included in dist
            
require("./typedef");
